import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import moment from 'moment';

import { toApiDate } from 'lib/dates';
import type { MutationFunction } from '@apollo/client';
import { Button, Stack, Typography } from '@mui/material';

import {
  BlockLoading,
  IMultiStepActions,
  useToastr,
} from '@farmshare/ui-components';
import type { IToastrContext } from '@farmshare/ui-components';

import {
  AnimalHeadInput,
  CreateProcessorSchedulingMutation,
  CreateProcessorSchedulingMutationVariables,
  CustomFieldValueBulkSaveMutation,
  CustomFieldValueBulkSaveMutationVariables,
  EnumProcessorSchedulingAnimalHeadsInspectionLevel,
  EnumProcessorSchedulingAnimalSpecies,
  useBookingDepositPaymentIntentCreateMutation,
  User,
  useViewProcessorAdminLightQuery,
} from '../../../lib/graphql';
import { persistBookingCustomFieldValues } from './quick-schedule-steps';
import { DepositPaymentFields } from './deposit-payment-fields';
import {
  isDepositApplicable,
  speciesDeposit,
} from 'features/jobs/helpers/species-deposit';

import {
  getSplitSlotsForType,
  ProcessorOption,
  ProcessorSelectionStep,
  QuickScheduleForm,
  ReviewStep,
  ScheduleDetailsStep,
  STEP_INTRO_DEPOSIT,
  STEP_INTRO_DETAILS,
  STEP_INTRO_REVIEW,
  StepIntro,
} from './quick-schedule';

/**
 * Wrapper that loads processor settings for the selected vendorId.
 * @param root0
 * @param root0.data
 * @param root0.actions
 */
function DetailsStepWrapper({
  data,
  actions,
}: {
  data: QuickScheduleForm;
  actions: IMultiStepActions<QuickScheduleForm>;
}) {
  const vendorId = data.selectedVendorId;
  const { data: settingsData, loading } = useViewProcessorAdminLightQuery({
    variables: { filter: { vendor: vendorId } },
    skip: !vendorId,
  });
  const processorSettings = settingsData?.processorSettingsOne;
  const hasSetSettingsId = React.useRef<string | null>(null);

  useEffect(() => {
    if (processorSettings?._id && hasSetSettingsId.current !== vendorId) {
      hasSetSettingsId.current = vendorId ?? null;
      actions.updateData({
        ...data,
        processorSettingsId: processorSettings._id,
      });
    }
  }, [processorSettings, vendorId, data, actions]);

  if (loading || !processorSettings || !vendorId) {
    return <BlockLoading />;
  }

  return (
    <Formik
      initialValues={data}
      validate={(values: QuickScheduleForm) => {
        const errors: Record<string, string> = {};
        if (!values?.species) errors.species = 'Species is required';
        if (!values?.quantity || values.quantity < 1)
          errors.quantity = 'Quantity must be at least 1';
        if (!values?.date) errors.date = 'Date is required';
        return errors;
      }}
      // Enter-key submit only ADVANCES the wizard (validation gates it). The
      // create runs once at completion via the guarded renderer `next()` ->
      // `onComplete` — never from a step form, so a stray form submit can't
      // create a duplicate job/animal set.
      onSubmit={() => actions.next()}
    >
      {({ values }) => {
        const valuesRef = React.useRef<QuickScheduleForm | null>(null);
        React.useEffect(() => {
          // Recompute whether the final Deposit step applies for the chosen
          // processor + species so it shows/hides live as the user edits.
          const merged: QuickScheduleForm = {
            ...values,
            depositApplicable: isDepositApplicable(
              processorSettings,
              values.species,
            ),
          };
          if (JSON.stringify(valuesRef.current) !== JSON.stringify(merged)) {
            valuesRef.current = merged;
            actions.updateData(merged);
          }
        }, [values]);

        return (
          <Form>
            <ScheduleDetailsStep
              vendorId={vendorId}
              processorSettings={processorSettings}
              onNext={actions.next}
            />
          </Form>
        );
      }}
    </Formik>
  );
}

function ReviewStepWrapper({
  data,
  actions,
}: {
  data: QuickScheduleForm;
  actions: IMultiStepActions<QuickScheduleForm>;
}) {
  const vendorId = data.selectedVendorId;
  const { data: settingsData, loading } = useViewProcessorAdminLightQuery({
    variables: { filter: { vendor: vendorId } },
    skip: !vendorId,
  });
  const processorSettings = settingsData?.processorSettingsOne;

  if (loading || !processorSettings || !vendorId) {
    return <BlockLoading />;
  }

  return (
    <Formik
      initialValues={data}
      enableReinitialize
      // Last step: an Enter-key submit routes through the guarded renderer
      // `next()` -> `onComplete` (which calls `handleSubmit` once), the same
      // single create path as the footer "Schedule Job" button. The renderer's
      // in-flight ref de-dupes Enter-then-click.
      onSubmit={() => actions.next()}
    >
      {({ values }) => {
        const valuesRef = React.useRef(values);
        React.useEffect(() => {
          if (JSON.stringify(valuesRef.current) !== JSON.stringify(values)) {
            valuesRef.current = values;
            actions.updateData(values);
          }
        }, [values]);

        return (
          <Form>
            <ReviewStep
              vendorId={vendorId}
              processorSettings={processorSettings}
            />
          </Form>
        );
      }}
    </Formik>
  );
}

const formatUsd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * Final "Deposit" step for the customer self-serve flow. "Pay first": mints a
 * job-less PaymentIntent and collects payment with self-rendered Stripe Elements
 * BEFORE the job is created (the create-on-complete verifies + attaches it).
 * Only rendered when the step applies (gated by the step's `visible` predicate
 * on `data.depositApplicable`), so it appears/disappears as the chosen
 * processor or species changes.
 * @param root0
 * @param root0.data
 * @param root0.actions
 * @param root0.confirmRef
 * @param root0.confirmRef.current
 */
function CustomerDepositStepWrapper({
  data,
  actions,
  confirmRef,
}: {
  data: QuickScheduleForm;
  actions: IMultiStepActions<QuickScheduleForm>;
  confirmRef: { current: (() => Promise<boolean>) | null };
}) {
  const { push } = useToastr();
  const vendorId = data.selectedVendorId;

  // Load the chosen vendor's per-species deposit (cached from the Review step).
  const { data: settingsData, loading: settingsLoading } =
    useViewProcessorAdminLightQuery({
      variables: { filter: { vendor: vendorId } },
      skip: !vendorId,
    });
  const schedulingDeposit = speciesDeposit(
    settingsData?.processorSettingsOne?.animalSettings,
    data.species,
  );
  const eligible = schedulingDeposit > 0;
  const amount = schedulingDeposit * (data.quantity ?? 0);

  const [createPaymentIntent] = useBookingDepositPaymentIntentCreateMutation();
  const [pi, setPi] = useState<{
    clientSecret: string;
    connectedAccountId: string;
  } | null>(null);
  const [mintFailed, setMintFailed] = useState(false);
  const mintedRef = React.useRef(false);

  // Mint the job-less PaymentIntent and stash its id on data so the
  // create-on-complete can verify + attach it. Extracted so a failure can be
  // retried (rather than dead-ending on an infinite spinner).
  const startPayment = React.useCallback(() => {
    if (!vendorId) return;
    mintedRef.current = true;
    setMintFailed(false);
    createPaymentIntent({
      variables: {
        vendorId,
        headCount: data.quantity,
        species: data.species,
        // Ties the charge to this booking so it can be recovered if the
        // create fails after the card step.
        requestId: data.requestId,
      },
    })
      .then((res) => {
        const d = res.data?.bookingDepositPaymentIntentCreate;
        if (!d?.clientSecret) {
          throw new Error('Could not start the deposit payment.');
        }
        setPi({
          clientSecret: d.clientSecret,
          connectedAccountId: d.connectedAccountId,
        });
        actions.updateData({ depositPaymentIntentId: d.paymentIntentId });
      })
      .catch((e: unknown) => {
        // Reset the guard and surface a retry instead of leaving the step
        // spinning forever on a failed mint.
        mintedRef.current = false;
        setMintFailed(true);
        push({
          title: 'Could not start payment',
          body: e instanceof Error ? e.message : 'Please try again.',
          severity: 'error',
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, data.quantity, data.species]);

  // Eligible → mint once when the step first applies.
  useEffect(() => {
    if (settingsLoading || !eligible || mintedRef.current || !vendorId) return;
    startPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading, eligible, vendorId]);

  if (!eligible) return settingsLoading ? <BlockLoading /> : null;
  if (mintFailed && !pi) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Typography color="error">
          We were unable to start the deposit payment. No charge was made.
        </Typography>
        <Button variant="outlined" onClick={startPayment}>
          Try again
        </Button>
      </Stack>
    );
  }
  if (!pi) return <BlockLoading />;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">
        Deposit{amount ? ` · ${formatUsd(amount)}` : ''}
      </Typography>
      <DepositPaymentFields
        clientSecret={pi.clientSecret}
        connectedAccountId={pi.connectedAccountId}
        confirmRef={confirmRef}
        onReadyChange={(ready) => actions.updateData({ depositReady: ready })}
        onUnavailable={() => actions.updateData({ depositReady: false })}
      />
    </Stack>
  );
}

export interface ProducerQuickScheduleConfigArgs {
  /** All processors the producer has a relationship with. */
  processors: ProcessorOption[];
  /** The signed-in producer user — used to pre-fill requester contact info. */
  user: NonNullable<User>;
  onSuccess?: (scheduleId: string) => void;
  createScheduling: MutationFunction<
    CreateProcessorSchedulingMutation,
    CreateProcessorSchedulingMutationVariables
  >;
  /**
   * Persists booking-time custom field values entered in the Review step
   *  after the schedule is created. Required so every quick-schedule
   *  entrypoint wires persistence consistently.
   */
  bulkSaveCustomFieldValues: MutationFunction<
    CustomFieldValueBulkSaveMutation,
    CustomFieldValueBulkSaveMutationVariables
  >;
  pushToast: IToastrContext['pushToast'];
  /** Optional initial date (YYYY-MM-DD). */
  initialDate?: string;
  /** Optional pre-selected processor — typically when launching from a specific landing page. */
  initialVendorId?: string;
  /** Optional pre-filled form values (species/quantity/block/etc.) — merged on top of defaults. */
  initialFormOverrides?: Partial<QuickScheduleForm>;
  /** Waitlist entry that this booking is fulfilling. Linked on the resulting job. */
  waitlistId?: string;
  /**
   * Set when a processor admin is booking on behalf of a producer. The
   * resulting job's `requesterContactInformation` is then attributed to the
   * producer rather than the authenticated admin.
   */
  isOnBehalfOf?: boolean;
}

export function createProducerQuickScheduleMultiStepConfig({
  processors,
  user,
  onSuccess,
  createScheduling,
  pushToast,
  bulkSaveCustomFieldValues,
  initialDate,
  initialVendorId,
  initialFormOverrides,
  waitlistId,
  isOnBehalfOf = false,
}: ProducerQuickScheduleConfigArgs) {
  const handleSubmit = async (
    formValues: QuickScheduleForm,
    processorSettingsId: string,
    vendorId: string,
  ) => {
    if (!vendorId || !processorSettingsId) {
      pushToast({
        title: 'Error',
        body: 'Missing required configuration',
        bg: 'danger',
      });
      return;
    }

    const animalHeads: AnimalHeadInput[] = Array.from(
      { length: formValues.quantity },
      (_, index) => {
        const cutsheetInformation: AnimalHeadInput['cutsheetInformation'] = [];
        if (formValues.splitTypes?.[index]) {
          const slots = getSplitSlotsForType(formValues.splitTypes[index]);
          slots.forEach((slot, slotIdx) => {
            const buyerId = formValues.splitAssignments?.[index]?.[slotIdx];
            const inlineBuyer = formValues.splitBuyers?.[index]?.[slotIdx];

            if (buyerId) {
              cutsheetInformation.push({
                splitPart: slot.splitPart,
                contactUser: buyerId,
                quantity: 1,
              });
            } else if (inlineBuyer?.first_name) {
              cutsheetInformation.push({
                splitPart: slot.splitPart,
                contactInformation: {
                  firstName: inlineBuyer.first_name,
                  lastName: inlineBuyer.last_name,
                  email: inlineBuyer.email,
                  phone: inlineBuyer.phone,
                  company: inlineBuyer.company,
                },
                quantity: 1,
              });
            }
          });
        }

        return {
          inspectionLevel: formValues.inspectionLevel,
          producerIdentifier: formValues.producerIdentifiers?.[index] || '',
          cutsheetInformation,
          ...(formValues.sexes?.[index] && { sex: formValues.sexes[index] }),
          ...(formValues.isOverThirtyMonths?.[index] !== undefined && {
            isOverThirtyMonths: formValues.isOverThirtyMonths[index],
          }),
          ...(formValues.isKillAndChill?.[index] !== undefined && {
            isKillAndChill: formValues.isKillAndChill[index],
          }),
          ...(formValues.splitTypes?.[index] && {
            splitType: formValues.splitTypes[index],
          }),
        };
      },
    );

    const requesterContactInformation = {
      producer: formValues.requesterContactInformation.producer,
      firstName: formValues.requesterContactInformation.first_name,
      lastName: formValues.requesterContactInformation.last_name,
      email: formValues.requesterContactInformation.email,
      phone: formValues.requesterContactInformation.phone,
      company: formValues.requesterContactInformation.company,
    };

    const dropoffDate = toApiDate(formValues.date);
    if (!dropoffDate) {
      pushToast({
        title: 'Invalid date',
        body: 'Please choose a valid drop-off date.',
        bg: 'danger',
      });
      return;
    }
    try {
      const result = await createScheduling({
        variables: {
          newSchedule: {
            requestId: formValues.requestId,
            animalSpecies: formValues.species,
            dropoffDate,
            headCount: formValues.quantity,
            processorSettings: processorSettingsId,
            vendor: vendorId,
            animalHeads,
            requesterContactInformation,
            refundDeadlineDays: 0,
            isOnBehalfOf,
            endDate: toApiDate(moment(formValues.date).add(6, 'days')),
            blockIndex: formValues.blockIndex,
            blockTitle: formValues.blockTitle,
            waitlist: waitlistId,
            // Self-serve "pay first": deposit already paid in the Deposit step.
            ...(formValues.depositPaymentIntentId
              ? { depositPaymentIntentId: formValues.depositPaymentIntentId }
              : {}),
          },
        },
      });

      const created = (
        result as {
          data?: {
            processorSchedulingCreate?: {
              scheduling?: {
                _id?: string;
                animalHeads?: Array<{ _id?: string } | null> | null;
              };
            };
          };
        }
      )?.data?.processorSchedulingCreate;
      const scheduling = created?.scheduling;
      const newScheduleId = scheduling?._id;
      // Any deposit was already paid in the Deposit step before this create, so
      // the job is created confirmed — nothing left to collect here.
      if (newScheduleId) {
        await persistBookingCustomFieldValues({
          formValues,
          jobId: newScheduleId,
          headIds:
            scheduling?.animalHeads?.map((h) => h?._id ?? undefined) ?? [],
          bulkSaveCustomFieldValues,
          pushToast,
        });

        pushToast({
          title: 'Success',
          body: 'Job scheduled successfully!',
          severity: 'success',
          showConfetti: true,
          action: {
            label: 'View Job',
            href: `/job/${newScheduleId}`,
          },
        });
        onSuccess?.(newScheduleId);
      } else {
        pushToast({
          title: 'Error',
          body: 'Failed to create schedule',
          severity: 'error',
        });
      }
    } catch (error) {
      // The Deposit step only AUTHORIZED the card; the server charges it once
      // the job is created. A failure here can land on either side of that
      // charge (or be a lost response to a create that finished), so make no
      // claim about whether the card was charged. Retrying reuses the same
      // PaymentIntent, which can only ever be charged once — so don't clear
      // `depositPaymentIntentId`.
      const baseMessage =
        error instanceof Error ? error.message : 'Failed to create schedule';
      pushToast({
        title: 'Error',
        body: formValues.depositPaymentIntentId
          ? `${baseMessage} Press Next to try again — your card won't be charged twice.`
          : baseMessage,
        severity: 'error',
      });
    }
  };

  // Resolve the initial selected vendor: caller-provided > single-processor > none.
  const resolvedInitialVendorId =
    initialVendorId ??
    (processors.length === 1 ? processors[0].vendorId : undefined);

  const initialFormValues: QuickScheduleForm = {
    // Idempotency token — stable per wizard mount (the renderer snapshots
    // initialData once). Overridable so a caller can supply their own.
    requestId: crypto.randomUUID(),
    selectedVendorId: resolvedInitialVendorId,
    requesterContactInformation: {
      producer: (user as { _id?: string })._id ?? '',
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
      company: user.company ?? '',
    },
    species: EnumProcessorSchedulingAnimalSpecies.Beef,
    inspectionLevel: EnumProcessorSchedulingAnimalHeadsInspectionLevel.Exempt,
    quantity: 1,
    date: initialDate || moment().add(1, 'day').format('YYYY-MM-DD'),
    blockTitle: '',
    blockIndex: undefined,
    producerIdentifiers: [],
    sexes: [],
    isOverThirtyMonths: [],
    isKillAndChill: [],
    splitTypes: [],
    splitAssignments: {},
    ...initialFormOverrides,
  };

  // Skip the in-modal selection step when there's only one processor OR the
  // caller already chose one (e.g. launching from a specific processor's tab).
  const showProcessorSelection =
    processors.length > 1 && !resolvedInitialVendorId;

  // Bridges the deposit step's body (inside Stripe Elements) to its "Pay" Next
  // button: the body registers a confirm handler, the step's onNext calls it.
  const depositConfirmRef: { current: (() => Promise<boolean>) | null } = {
    current: null,
  };

  // The processor step only exists when a producer works with more than one
  // facility, so every later step's number depends on it. Derived once here
  // rather than hard-coded, which would mislabel every screen for a producer
  // with a single processor.
  const stepOffset = showProcessorSelection ? 1 : 0;

  const steps = [
    ...(showProcessorSelection
      ? [
          {
            title: 'Select Processor',
            body: (
              data: QuickScheduleForm,
              actions: IMultiStepActions<QuickScheduleForm>,
            ) => (
              <ProcessorSelectionStep
                processors={processors}
                stepNumber={1}
                selectedVendorId={data.selectedVendorId}
                onSelect={(vendorId) => {
                  // Commit synchronously to multi-step data, then advance —
                  // otherwise the next step renders before selectedVendorId
                  // propagates and its settings query gets skipped.
                  // Don't seed processorSettingsId here: an explicit `undefined`
                  // gets snapshotted into Formik's initialValues on the next
                  // step and then clobbered back over the parent data on every
                  // keystroke via the values-sync effect.
                  actions.updateData({ selectedVendorId: vendorId });
                  actions.next();
                }}
              />
            ),
            canGoNext: (data: QuickScheduleForm) =>
              Boolean(data?.selectedVendorId),
          },
        ]
      : []),
    {
      title: 'Details & Date',
      body: (
        data: QuickScheduleForm,
        actions: IMultiStepActions<QuickScheduleForm>,
      ) => (
        <>
          <StepIntro intro={STEP_INTRO_DETAILS} number={stepOffset + 1} />
          <DetailsStepWrapper data={data} actions={actions} />
        </>
      ),
      canGoNext: (data: QuickScheduleForm) =>
        Boolean(
          data?.quantity &&
            data?.species &&
            data?.inspectionLevel &&
            data?.date &&
            data?.blockTitle,
        ),
    },
    {
      title: 'Review',
      body: (
        data: QuickScheduleForm,
        actions: IMultiStepActions<QuickScheduleForm>,
      ) => (
        <>
          <StepIntro intro={STEP_INTRO_REVIEW} number={stepOffset + 2} />
          <ReviewStepWrapper data={data} actions={actions} />
        </>
      ),
      // When no deposit applies, Review is the final step — submit from here.
      nextButtonText: (data: QuickScheduleForm) =>
        data.depositApplicable ? 'Next' : 'Schedule Job',
      canGoNext: (data: QuickScheduleForm) => {
        if (data?.splitTypes && data.splitTypes.length > 0) {
          for (let i = 0; i < data.quantity; i++) {
            if (data.splitTypes[i]) {
              const slots = getSplitSlotsForType(data.splitTypes[i]);
              for (let slotIdx = 0; slotIdx < slots.length; slotIdx++) {
                const hasBuyerId = Boolean(
                  data.splitAssignments?.[i]?.[slotIdx],
                );
                const inlineBuyer = data.splitBuyers?.[i]?.[slotIdx];

                if (!hasBuyerId && inlineBuyer) {
                  const isInlineBuyerComplete =
                    Boolean(inlineBuyer.first_name) &&
                    (Boolean(inlineBuyer.email) || Boolean(inlineBuyer.phone));
                  if (!isInlineBuyerComplete) return false;
                }
              }
            }
          }
        }
        return true;
      },
    },
    {
      title: 'Deposit',
      // Shown only when the chosen processor + species takes a deposit; toggles
      // live as the processor or species changes in the earlier steps.
      visible: (data: QuickScheduleForm) => Boolean(data.depositApplicable),
      body: (
        data: QuickScheduleForm,
        actions: IMultiStepActions<QuickScheduleForm>,
      ) => (
        <>
          <StepIntro intro={STEP_INTRO_DEPOSIT} number={stepOffset + 3} />
          <CustomerDepositStepWrapper
            data={data}
            actions={actions}
            confirmRef={depositConfirmRef}
          />
        </>
      ),
      nextButtonText: 'Pay deposit',
      // Card fields must be mounted before paying.
      canGoNext: (data: QuickScheduleForm) => Boolean(data.depositReady),
      onNext: async () => {
        const confirm = depositConfirmRef.current;
        if (!confirm) return; // nothing to collect — proceed to create
        const ok = await confirm();
        if (!ok) throw new Error('DEPOSIT_PAYMENT_FAILED');
      },
    },
  ];

  return {
    // No title, like the processor's Schedule Job wizard: the step chevrons
    // already say what this is, and without a title bar above them the
    // renderer promotes the stepper to the dialog's own flush header.
    size: 'sm' as const,
    // The dialog still needs a name for screen readers now that nothing
    // visible supplies one.
    ariaLabel: 'Schedule a Job',
    // With no header there's no close X, so the footer carries the exit on
    // step one — every later step has Back to walk out through.
    showCancelOnFirstStep: true,
    initialData: initialFormValues,
    onComplete: async (data: QuickScheduleForm) => {
      const { processorSettingsId, selectedVendorId } = data;
      if (!processorSettingsId || !selectedVendorId) {
        pushToast({
          title: 'Error',
          body: 'Unable to load processor settings',
          severity: 'error',
        });
        return;
      }
      await handleSubmit(data, processorSettingsId, selectedVendorId);
    },
    steps,
  };
}
